import { searchService } from "@/modules/hub/features/Search/services/searchService";
import type {
  FetchEventSearchResultsRequest,
  SearchResultEvent,
  EventSearchFilters,
} from "../api/fetchEventSearchResults/types";

const stringToId = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

class EventDirectoryAlgoliaService {
  async searchEvents(params: FetchEventSearchResultsRequest): Promise<{
    events: SearchResultEvent[];
    totalCount: number;
    facets: EventSearchFilters;
  }> {
    await searchService.initialize(false);

    const algoliaFilters: any = {};

    if (params.eventTypeNames && params.eventTypeNames.length > 0) {
      algoliaFilters.eventTypes = params.eventTypeNames;
    }

    if (params.categoryNames && params.categoryNames.length > 0) {
      algoliaFilters.eventCategories = params.categoryNames;
    }

    if (params.tradeNames && params.tradeNames.length > 0) {
      algoliaFilters.trades = params.tradeNames;
    }

    if (params.businessRoleNames && params.businessRoleNames.length > 0) {
      algoliaFilters.businessRoles = params.businessRoleNames;
    }

    if (params.showOnlyTotalAccess) {
      algoliaFilters.isEligibleForLata = true;
    }

    const algoliaPage = (params.page || 1) - 1;
    const hitsPerPage = params.pageSize || 12;
    const useFavoritesKey = params.showFavorites === true;

    // Target the date-sorted replica so the directory grid sorts by next
    // upcoming session date ascending (configured in the Algolia dashboard
    // as the `eventsByNextSessionAsc` virtual replica). Falls back to the
    // primary index if the replica name isn't yet present in the config —
    // which can happen briefly during deploys before the backend update lands.
    const indexNameOverride =
      searchService.getIndices()?.eventsByNextSessionAsc;

    const result = await searchService.searchEvents(
      params.searchTerm || "",
      algoliaFilters,
      algoliaPage,
      hitsPerPage,
      useFavoritesKey,
      indexNameOverride,
    );

    const events: SearchResultEvent[] = result.hits.map((hit: any) => ({
      id: hit.eventId || parseInt(hit.objectID?.replace("event_", "") || "0"),
      uuid: hit.uuid || "",
      eventCode: hit.eventCode || "",
      eventName: hit.eventName || "",
      eventDescription: hit.eventDescription || "",
      eventPrice: hit.eventPrice || 0,
      isPublished: hit.isPublished || false,
      thumbnailUrl: hit.thumbnail || null,
      eventTypeName: hit.eventType || null,
      eventCategoryNames: hit.eventCategories || [],
      createdAt: hit.createdAt || null,
      viewCount: hit.viewCount || 0,
      isVoucherEligible: hit.isVoucherEligible || false,
      isEligibleForLata: hit.isEligibleForLata || false,
      redirectToLegacy: Boolean(hit.redirectToLegacy ?? true),
      legacyCourseId:
        typeof hit.legacyCourseId === "number" ? hit.legacyCourseId : null,
      sessions: (hit.sessions || []).map((s: any) => ({
        name: s.name || "",
        startDate: s.startDate || null,
        endDate: s.endDate || null,
        venue: s.venue || null,
        location: s.location || null,
        isVirtual: s.isVirtual || false,
        instructor: s.instructor || null,
      })),
      nextSessionStartDate:
        typeof hit.nextSessionStartDate === "number"
          ? hit.nextSessionStartDate
          : null,
    }));

    // Cross-page sort by `nextSessionStartDate` is now owned by the
    // `eventsByNextSessionAsc` Algolia replica — no client-side resort needed.

    const facets: EventSearchFilters = {
      eventTypes: Object.entries(result.facets?.eventType || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      categories: Object.entries(result.facets?.eventCategories || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      trades: Object.entries(result.facets?.trades || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      businessRoles: Object.entries(result.facets?.businessRoles || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
    };

    return {
      events,
      totalCount: result.total,
      facets,
    };
  }

  async getMetadata(): Promise<EventSearchFilters> {
    await searchService.initialize(false);

    const result = await searchService.searchEvents("", {}, 0, 0);

    return {
      eventTypes: Object.entries(result.facets?.eventType || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      categories: Object.entries(result.facets?.eventCategories || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      trades: Object.entries(result.facets?.trades || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      businessRoles: Object.entries(result.facets?.businessRoles || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
    };
  }
}

export const eventDirectoryAlgoliaService = new EventDirectoryAlgoliaService();
